import React, { useState, useEffect } from 'react';
import { Button, Input, SelectBox, Text, Label, Modal, ModalInner } from 'components';
import { DuplicateOutlineIcon, InformationCircleOutlineIcon } from 'assets/svgs';

interface EOPRow {
  mjd: number;
  date: string;
  utc: string;
  xp_arcsec: number;
  yp_arcsec: number;
  dut1_sec: number;
  lod_sec: number;
  dpsi_arcsec: number;
  deps_arcsec: number;
  dx_arcsec: number;
  dy_arcsec: number;
  dat_tai_minus_utc_sec: number;
}

const TimeConversion = () => {
  const [timeFormat, setTimeFormat] = useState(1);
  const [inputTime, setInputTime] = useState('');
  const [convertedTimes, setConvertedTimes] = useState({
    ut1: '',
    dut1: '',
    tai: '',
    dat: '',
    tt: '',
    gps: '',
    jc: '',
    tle: ''
  });
  const [eopData, setEopData] = useState<EOPRow[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // EOP 데이터 로드
  useEffect(() => {
    const loadEOP = async () => {
      try {
        const response = await fetch('/eop.json');
        const data = await response.json();
        setEopData(data);
      } catch (error) {
        console.error('Failed to load EOP data:', error);
      }
    };
    loadEOP();
  }, []);

  const timeFormatOptions = [
    { value: 1, label: 'UTC' },
    { value: 2, label: 'UT1' },
    { value: 3, label: 'TAI' },
    { value: 4, label: 'TT' },
    { value: 5, label: 'GPS Time' },
    { value: 6, label: 'Julian Century' },
    { value: 7, label: 'Epoch Time(TLE)' }
  ];

  // 선택된 timeFormat에 따른 placeholder
  const getPlaceholder = () => {
    const format = timeFormatOptions.find(opt => opt.value === timeFormat);
    if (!format) return 'Enter time value';
    
    switch (format.label) {
      case 'UTC':
        return 'yyyy-mm-dd hh:mm:ss.sss (UTC)';
      case 'UT1':
        return 'yyyy-mm-dd hh:mm:ss.sss (UT1)';
      case 'TAI':
        return 'yyyy-mm-dd hh:mm:ss.sss (TAI)';
      case 'TT':
        return 'yyyy-mm-dd hh:mm:ss.sss (TT)';
      case 'GPS Time':
        return 'yyyy-mm-dd hh:mm:ss.sss (GPS Time)';
      case 'Julian Century':
        return '0.242026031505';
      case 'Epoch Time(TLE)':
        return '24075.0.49999999';
      default:
        return 'Enter time value';
    }
  };

  // UTC 파싱 (ISO-like 문자열)
  const parseUTC = (s: string) => {
    if (!s) return null;
    // 앞뒤 공백 제거
    let trimmed = s.trim();
    
    // 이미 T가 있는 경우 (ISO 형식)
    if (trimmed.includes("T")) {
      const iso = /[zZ]$/.test(trimmed) ? trimmed : trimmed + "Z";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    }
    
    // 공백이 있는 경우: 공백을 T로 변환
    if (trimmed.includes(" ")) {
      const iso = trimmed.replace(/\s+/, "T") + "Z";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    }
    
    // 공백도 T도 없는 경우: 날짜와 시간 사이에 T 삽입
    // 형식: YYYY-MM-DDHH:MM:SS 또는 YYYY-MM-DD HH:MM:SS
    // 날짜 패턴: YYYY-MM-DD 다음에 시간 패턴: HH:MM:SS
    const dateTimeMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})(\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/);
    if (dateTimeMatch) {
      const iso = `${dateTimeMatch[1]}T${dateTimeMatch[2]}Z`;
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    }
    
    // 다른 형식 시도
    const iso = trimmed + "Z";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  // 날짜를 Julian Day로 변환
  const dateToJD = (d: Date) => d.getTime() / 86400000 + 2440587.5;
  const dateToMJD = (d: Date) => d.getTime() / 86400000 + 40587.0;

  // Day of year 계산
  const dayOfYear = (d: Date) => {
    const y = d.getUTCFullYear();
    return Math.floor((d.getTime() - Date.UTC(y, 0, 1)) / 86400000) + 1;
  };

  // Zero padding
  const z = (n: number, w = 2) => n.toString().padStart(w, "0");

  // 날짜를 YMDHMSms 형식으로 포맷
  const fmtYMDHMSms = (ms: number) => {
    const d = new Date(ms);
    return (
      `${d.getUTCFullYear()}-${z(d.getUTCMonth() + 1)}-${z(d.getUTCDate())} ` +
      `${z(d.getUTCHours())}:${z(d.getUTCMinutes())}:${z(d.getUTCSeconds())}.${z(
        d.getUTCMilliseconds(),
        3
      )}`
    );
  };

  // 시간 변환 계산 (간단한 버전 - 실제로는 EOP 데이터 필요)
  const computeTimeScales = (utc: Date) => {
    // 2024년 기준으로 DAT와 DUT1 값 설정
    // 실제로는 날짜에 따라 달라지지만, 간단히 2024년 평균값 사용
    const DAT = 37; // 2024년에는 UTC와 TAI의 차이가 약 37초
    const DUT1 = -0.01; // 2024년 3월 기준 대략 -0.01초

    const out: any = {
      ut1: "",
      dut1: "",
      tai: "",
      dat: "",
      tt: "",
      gps: "",
      jc: "",
      tle: ""
    };

    // UT1 = UTC + DUT1 (DUT1은 음수일 수 있음)
    const ut1Ms = utc.getTime() + DUT1 * 1000;
    out.ut1 = fmtYMDHMSms(ut1Ms);
    out.dut1 = `${DUT1.toFixed(6)} s`;

    // TAI = UTC + DAT
    const taiMs = utc.getTime() + DAT * 1000;
    out.tai = fmtYMDHMSms(taiMs);
    out.dat = `${DAT.toFixed(0)} s`;

    // TT = TAI + 32.184 s
    const ttMs = taiMs + 32184;
    out.tt = fmtYMDHMSms(ttMs);

    // GPS = UTC + (DAT - 19) s
    const gpsMs = utc.getTime() + (DAT - 19) * 1000;
    out.gps = fmtYMDHMSms(gpsMs);

    // Julian Century of TT
    const jdTT = dateToJD(utc) + (DAT + 32.184) / 86400;
    out.jc = ((jdTT - 2451545.0) / 36525.0).toFixed(12);

    // TLE epoch: YYDDD.dddddddd
    const yy = utc.getUTCFullYear() % 100;
    const ddd = dayOfYear(utc);
    const sod =
      utc.getUTCHours() * 3600 +
      utc.getUTCMinutes() * 60 +
      utc.getUTCSeconds() +
      utc.getUTCMilliseconds() / 1000;
    out.tle = `${z(yy)}${z(ddd, 3)}.${(sod / 86400).toFixed(8)}`;

    return out;
  };

  // Julian Century에서 UTC로 변환
  const julianCenturyToUTC = (jc: string) => {
    // 공백 제거
    const trimmed = jc.trim().replace(/\s+/g, '');
    const jcValue = parseFloat(trimmed);
    if (isNaN(jcValue)) return null;
    
    // Julian Century로부터 Julian Day 계산
    const jd = 2451545.0 + (jcValue * 36525.0);
    
    // Julian Day에서 Date 객체로 변환 (미묘하게 다를 수 있음)
    const dayOfYear = (jd - Math.floor(jd)) * 365.25;
    const year = Math.floor(jd - 2451545.0) / 365.25 + 2000;
    
    // 간단한 변환
    const jdDays = jd - 2440587.5;
    const ms = jdDays * 86400000;
    return new Date(ms);
  };

  // Epoch Time(TLE)에서 UTC로 변환
  const tleToUTC = (tle: string) => {
    // 공백 제거
    const trimmed = tle.trim().replace(/\s+/g, '');
    // 형식: YYDDD.dddddddd 또는 24075.0.49999999
    const match = trimmed.match(/^(\d{2})(\d{3})\.(\d+)\.(\d+)$/);
    if (match) {
      const yy = parseInt(match[1]);
      const ddd = parseInt(match[2]);
      const dayFraction = parseFloat(match[3] + '.' + match[4]);
      
      // 2000년 기준
      const year = 2000 + yy;
      
      // 첫 날의 타임스탬프
      const jan1 = Date.UTC(year, 0, 1);
      const ms = jan1 + ((ddd - 1 + dayFraction) * 86400000);
      
      return new Date(ms);
    }
    return null;
  };

  const handleConvert = () => {
    // 공백 제거
    const trimmedTime = inputTime?.trim() || '';
    
    if (!trimmedTime) {
      setConvertedTimes({
        ut1: '',
        dut1: '',
        tai: '',
        dat: '',
        tt: '',
        gps: '',
        jc: '',
        tle: ''
      });
      return;
    }

    let d: Date | null = null;
    
    // 선택된 형식에 따라 파싱
    const format = timeFormatOptions.find(opt => opt.value === timeFormat);
    
    if (format?.label === 'Julian Century') {
      d = julianCenturyToUTC(trimmedTime);
    } else if (format?.label === 'Epoch Time(TLE)') {
      d = tleToUTC(trimmedTime);
    } else {
      d = parseUTC(trimmedTime);
    }

    if (d) {
      const result = computeTimeScales(d);
      setConvertedTimes(result);
    } else {
      // 입력이 올바르지 않은 경우
      setAlertMessage('Please enter a valid time format');
    }
  };

  const handleClear = () => {
    setInputTime('');
    setConvertedTimes({
      ut1: '',
      dut1: '',
      tai: '',
      dat: '',
      tt: '',
      gps: '',
      jc: '',
      tle: ''
    });
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      // 복사 성공 (필요시 알림 추가 가능)
      console.log('Copied:', value);
    } catch (err) {
      console.error('Failed to copy:', err);
      setAlertMessage('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-6 px-4 py-6">
      {alertMessage && (
        <Modal isOpened={true}>
          <ModalInner title="Notice" onClickClose={() => setAlertMessage(null)} className="md:w-[400px]">
            <div className="p-4 pt-0">
              <p className="text-body1 text-center" style={{ whiteSpace: 'pre-wrap' }}>
                {alertMessage}
              </p>
            </div>
            <div className="flex w-full flex-row items-center justify-end bg-bg-transparent_gray_light p-4">
              <Button className="h-9" text="OK" onClick={() => setAlertMessage(null)} size="large" />
            </div>
          </ModalInner>
        </Modal>
      )}
      {/* Input Section */}
      <div className="bg-gray-50 rounded-lg">
        <div className="space-y-4">
          <SelectBox
            className="w-full max-w-full"
            bgColor="layer3"
            label="Time Format"
            list={timeFormatOptions}
            value={timeFormat}
            setValue={setTimeFormat}
          />
          <Input
            type="text"
            className="bg-white"
            label=""
            placeholder={getPlaceholder()}
            value={inputTime}
            setValue={setInputTime}
          />
          <div className="flex gap-4">
            <Button
              type="blue"
              text="Convert"
              size="large"
              onClick={handleConvert}
              className="flex-1 items-center justify-center"
            />
            <Button
              type="secondary"
              text="Clear"
              size="large"
              onClick={handleClear}
              className="flex-1 items-center justify-center"
            />
          </div>
        </div>
      </div>

      {/* Converted Time Results */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <Text size="heading3" weight="medium" color="black" className="mb-4">
          Converted Time
        </Text>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <Text size="body2" weight="medium" color="primary">
                  UT1 (= UTC + DUT1) {convertedTimes.dut1 && `(${convertedTimes.dut1})`}
                </Text>
                <Text size="body1" weight="regular" color="black">
                  {convertedTimes.ut1 || '-'}
                </Text>
              </div>
              <button
                onClick={() => convertedTimes.ut1 && handleCopy(convertedTimes.ut1)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!convertedTimes.ut1}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <Text size="body2" weight="medium" color="primary">
                  TAI (= UTC + DAT) {convertedTimes.dat && `(${convertedTimes.dat})`}
                </Text>
                <Text size="body1" weight="regular" color="black">
                  {convertedTimes.tai || '-'}
                </Text>
              </div>
              <button
                onClick={() => convertedTimes.tai && handleCopy(convertedTimes.tai)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!convertedTimes.tai}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <Text size="body2" weight="medium" color="primary">
                  TT
                </Text>
                <Text size="body1" weight="regular" color="black">
                  {convertedTimes.tt || '-'}
                </Text>
              </div>
              <button
                onClick={() => convertedTimes.tt && handleCopy(convertedTimes.tt)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!convertedTimes.tt}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <Text size="body2" weight="medium" color="primary">
                  Julian Century
                </Text>
                <Text size="body1" weight="regular" color="black">
                  {convertedTimes.jc || '-'}
                </Text>
              </div>
              <button
                onClick={() => convertedTimes.jc && handleCopy(convertedTimes.jc)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!convertedTimes.jc}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <Text size="body2" weight="medium" color="primary">
                  GPS Time
                </Text>
                <Text size="body1" weight="regular" color="black">
                  {convertedTimes.gps || '-'}
                </Text>
              </div>
              <button
                onClick={() => convertedTimes.gps && handleCopy(convertedTimes.gps)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!convertedTimes.gps}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <Text size="body2" weight="medium" color="primary">
                  Epoch Time(TLE)
                </Text>
                <Text size="body1" weight="regular" color="black">
                  {convertedTimes.tle || '-'}
                </Text>
              </div>
              <button
                onClick={() => convertedTimes.tle && handleCopy(convertedTimes.tle)}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!convertedTimes.tle}
              >
                <DuplicateOutlineIcon />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Time Unit Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <InformationCircleOutlineIcon />
          <Text size="heading3" weight="medium" color="black">
            Time Unit Information
          </Text>
        </div>
        
        <div className="space-y-3">
          <div>
            <Text size="body2" weight="medium" color="black">
              UTC (Coordinated Universal Time):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Global standard time, adjusted with leap seconds.
            </Text>
          </div>
          
          <div>
            <Text size="body2" weight="medium" color="black">
              UT1 (Universal Time 1):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Time based on Earth&apos;s actual rotation.
            </Text>
          </div>
          
          <div>
            <Text size="body2" weight="medium" color="black">
              TAI (International Atomic Time):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Uniform atomic time scale, without leap seconds.
            </Text>
          </div>
          
          <div>
            <Text size="body2" weight="medium" color="black">
              TT (Terrestrial Time):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Terrestrial reference time for astronomy (TAI + 32.184s).
            </Text>
          </div>
          
          <div>
            <Text size="body2" weight="medium" color="black">
              Julian Century:
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Time scale in centuries since J2000 epoch.
            </Text>
          </div>
          
          <div>
            <Text size="body2" weight="medium" color="black">
              GPS Time:
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Time used by GPS satellites, no leap seconds (TAI - 19s).
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeConversion;
